
class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
import { PopIn } from 'theme';
// Ensure PopIn is available as a global object
class ComparisonPopup extends PopIn {
  constructor() {
    super();
    this.thisComp = this.querySelector('.comp-wrap');
    this.slider = this.querySelector('[id^="swiper-"]');

    if (this.slider) {
      this.initSwiper();
    }

    // Listen for the variant:change event
    document.addEventListener("variant:change", this.onVariantChange.bind(this));

    // Listen for toggle-ajax button clicks
    this.addEventListener("click", this.onToggleAjaxClick.bind(this));

    // Check for the 'show_size=true' URL parameter to show the popin automatically
    if (this.shouldAppearAutomatically) {
      setTimeout(() => this.show(), this.apparitionDelay);
    }
  }

  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }

  get shouldAppearAutomatically() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("show_size") === "true";
  }

  onVariantChange(event) {
    const selectedVariant = event.detail.variant;
    const productHandle = this.querySelector('.product-data').dataset.handle;
    const varUrl = `/products/${productHandle}?variant=${selectedVariant.id}`;

    this.fetchAndUpdateComparison(varUrl);
  }

  onToggleAjaxClick(event) {
    const button = event.target.closest(".toggle-ajax");
    if (!button) return;

    const productUrl = button.dataset.product;
    if (!productUrl) return;

    this.fetchAndUpdateComparison(productUrl);
  }

  fetchAndUpdateComparison(url) {
    // Store the current active slide index
    let currentSlideIndex = this.swiperInstance?.activeIndex || 0;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const newCompContent = responseHTML.querySelector('comparison-popup .comp-wrap');

        if (newCompContent) {
          const existingWaypointDiv = this.thisComp.querySelector('.comp-main--toggle-waypoints');
          const newWaypointDiv = newCompContent.querySelector('.comp-main--toggle-waypoints');

          if (existingWaypointDiv && newWaypointDiv) {
            // Preserve the existing waypoint div
            newWaypointDiv.replaceWith(existingWaypointDiv);

            // Switch the active class between the .toggle-waypoint buttons
            const buttons = existingWaypointDiv.querySelectorAll('.toggle-waypoint');
            if (buttons.length === 2) {
              buttons[0].classList.toggle('active');
              buttons[1].classList.toggle('active');
            }
          }

          // Update everything else
          HTMLUpdateUtility.setInnerHTML(this.thisComp, newCompContent.innerHTML);
          
          // Reinitialize Swiper if needed
          this.slider = this.thisComp.querySelector('[id^="swiper-"]');
          if (this.slider) {
            this.initSwiper();

            // Ensure swiperInstance is set before accessing it
            if (this.swiperInstance) {
              this.swiperInstance.slideTo(currentSlideIndex, 0, false); // Restore slide position
            }
          }
        }
      });
}


  

  initSwiper() {
    const slides = this.slider?.querySelectorAll('.swiper-slide');
    if (!slides || slides.length <= 1) {
      return;
    }

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    this.swiperInstance = new Swiper(this.slider, {
      loop: true,
      slidesPerView: 1.1,
      spaceBetween: 6,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      breakpoints: {
        700: {
          slidesPerView: 1
        }
      }
    });
  }
}

customElements.define("comparison-popup", ComparisonPopup);

// Define a new custom element for complementary products
class ComplementaryProducts extends HTMLElement {
  constructor() {
    super();
    
    // Listen for variant change events
    document.addEventListener("variant:change", this.onVariantChange.bind(this));
  }
  
  connectedCallback() {
    // Make sure the element is visible
    this.hidden = false;
  }
  
  onVariantChange(event) {
    const selectedVariant = event.detail.variant;
    //console.log(selectedVariant.title);
    
    // Get the product handle from the URL path
    const productHandle = window.location.pathname.split('/').filter(Boolean).pop();
    
    // Build the URL for fetching updated content
    const varUrl = `/products/${productHandle}?variant=${selectedVariant.id}`;
    
    // Update the complementary products
    this.fetchAndUpdateProducts(varUrl);
  }
  
  fetchAndUpdateProducts(url) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        // Always get the current container reference, not stored from constructor
        const productsContainer = this.querySelector('.complementary-products');
        
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const newProductsContent = responseHTML.querySelector('complementary-products .complementary-products');
        
        if (newProductsContent && productsContainer) {
          try {
            // Use a more direct approach rather than relying on HTMLUpdateUtility
            // This way we won't lose reference to our elements
            const newContent = newProductsContent.innerHTML;
            productsContainer.innerHTML = newContent;
          } catch (error) {
            console.error("Error directly updating HTML:", error);
          }
        } else {
          console.warn("Could not find elements to update", {
            currentContainer: !!productsContainer,
            newContent: !!newProductsContent
          });
        }
      })
      .catch(error => {
        console.error("Error updating complementary products:", error);
      });
  }
}

// Register the custom element
customElements.define("complementary-products", ComplementaryProducts);




// Production: Mega Menu & Mobile Sidebar Video Handler
document.addEventListener('DOMContentLoaded', function() {
  
  // Handle mega menu videos
  setupMegaMenuVideos();
  
  // Handle mobile sidebar videos
  setupMobileSidebarVideos();
  
  function setupMegaMenuVideos() {
    const megaMenus = document.querySelectorAll('mega-menu-disclosure');
    
    megaMenus.forEach((menuElement) => {
      const detailsElement = menuElement.querySelector('details');
      if (!detailsElement) return;
      
      const videos = menuElement.querySelectorAll('.deferred-video[data-src]');
      if (videos.length === 0) return;
      
      // Watch for menu open/close state changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
            const isOpen = detailsElement.getAttribute('aria-expanded') === 'true';
            
            if (isOpen) {
              loadAndPlayVideos(videos);
            } else {
              pauseVideos(videos);
            }
          }
        });
      });
      
      observer.observe(detailsElement, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
      
      // Hover preloading for hover-triggered menus
      if (menuElement.getAttribute('trigger') === 'hover') {
        let hoverTimeout;
        
        detailsElement.addEventListener('mouseenter', () => {
          clearTimeout(hoverTimeout);
          hoverTimeout = setTimeout(() => {
            if (videos.length > 0) {
              preloadVideo(videos[0]);
            }
          }, 150);
        });
        
        detailsElement.addEventListener('mouseleave', () => {
          clearTimeout(hoverTimeout);
        });
      }
    });
  }
  
  function setupMobileSidebarVideos() {
    const sidebar = document.querySelector('header-sidebar');
    if (!sidebar) return;
    
    // Find all buttons that control panels with videos
    const panelButtons = sidebar.querySelectorAll('button[aria-controls]');
    
    panelButtons.forEach((button) => {
      const panelId = button.getAttribute('aria-controls');
      const panel = sidebar.querySelector(`#${panelId}`);
      
      if (!panel) return;
      
      // Find videos in this panel
      const videos = panel.querySelectorAll('.deferred-video[data-src]');
      if (videos.length === 0) return;
      
      // Watch for panel open/close state changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
            const isOpen = button.getAttribute('aria-expanded') === 'true';
            
            if (isOpen) {
              loadAndPlayVideos(videos);
            } else {
              pauseVideos(videos);
            }
          }
        });
      });
      
      observer.observe(button, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    });
    
    // Also handle the main sidebar open/close
    const mainPanelVideos = sidebar.querySelectorAll('.header-sidebar__main-panel .deferred-video[data-src]');
    if (mainPanelVideos.length > 0) {
      const sidebarObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            const isOpen = sidebar.hasAttribute('open');
            
            if (isOpen) {
              // Sidebar opened - preload first video
              if (mainPanelVideos.length > 0) {
                preloadVideo(mainPanelVideos[0]);
              }
            } else {
              // Sidebar closed - pause all videos
              pauseVideos(mainPanelVideos);
              
              // Also pause videos in all sub-panels
              const allSidebarVideos = sidebar.querySelectorAll('.deferred-video[data-src]');
              pauseVideos(allSidebarVideos);
            }
          }
        });
      });
      
      sidebarObserver.observe(sidebar, {
        attributes: true,
        attributeFilter: ['open']
      });
    }
  }
  
  function loadAndPlayVideos(videos) {
    videos.forEach((video) => {
      initializeVideo(video, true);
    });
  }
  
  function pauseVideos(videos) {
    videos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  }
  
  function preloadVideo(video) {
    initializeVideo(video, false);
  }
  
  function initializeVideo(video, shouldPlay = false) {
    const videoSrc = video.getAttribute('data-src');
    if (!videoSrc) return;
    
    // Skip if already initialized
    if (video.querySelectorAll('source').length > 0) {
      if (shouldPlay && video.paused) {
        video.play().catch(() => {
          // Autoplay failed, but that's okay
        });
      }
      return;
    }
    
    // Create and add video source
    const source = document.createElement('source');
    source.src = videoSrc;
    source.type = 'video/mp4';
    video.appendChild(source);
    
    // Load the video
    video.load();
    
    // Handle successful load
    video.addEventListener('loadeddata', () => {
      // Hide fallback image
      const fallbackImage = video.parentNode.querySelector('.video-fallback');
      if (fallbackImage) {
        fallbackImage.style.transition = 'opacity 0.3s ease';
        fallbackImage.style.opacity = '0';
        setTimeout(() => {
          fallbackImage.style.display = 'none';
        }, 300);
      }
      
      // Auto-play if requested
      if (shouldPlay) {
        video.play().catch(() => {
          // Autoplay failed, but video is loaded
        });
      }
    });
    
    // Handle load errors silently
    video.addEventListener('error', () => {
      // Keep fallback image visible
    });
  }
  
});
// Define a new custom element for feature media lineup
    class FeatureMediaLineup extends HTMLElement {
      constructor() {
        super();
        
        // Listen for variant change events
        document.addEventListener("variant:change", this.onVariantChange.bind(this));
      }
      
      connectedCallback() {
        // Make sure the element is visible
        this.hidden = false;
      }
      
      onVariantChange(event) {
        const selectedVariant = event.detail.variant;
        //console.log('Feature lineup variant changed:', selectedVariant.title);
        
        // Get the product handle from the URL path
        const productHandle = window.location.pathname.split('/').filter(Boolean).pop();
        
        // Build the URL for fetching updated content
        const varUrl = `/products/${productHandle}?variant=${selectedVariant.id}`;
        
        // Update the feature media lineup
        this.fetchAndUpdateFeatures(varUrl);
      }
      
      fetchAndUpdateFeatures(url) {
        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            // Always get the current container reference
            const currentSection = this.querySelector('.section-spacing');
            
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const newSectionContent = responseHTML.querySelector('feature-media-lineup .section-spacing');
            
            if (newSectionContent && currentSection) {
              try {
                // Update the content directly
                const newContent = newSectionContent.innerHTML;
                currentSection.innerHTML = newContent;
              } catch (error) {
                console.error("Error updating feature media lineup:", error);
              }
            } else if (!newSectionContent) {
              // If no new content is found, it means the variant doesn't have the metafield
              // Hide the entire section
              this.style.display = 'none';
            } else {
              console.warn("Could not find elements to update", {
                currentSection: !!currentSection,
                newContent: !!newSectionContent
              });
            }
          })
          .catch(error => {
            console.error("Error updating feature media lineup:", error);
          });
      }
    }

    // Register the custom element
    customElements.define("feature-media-lineup", FeatureMediaLineup);

// Wait for the original MultipleMediaWithText to be defined first
function waitForBaseElement() {
  return new Promise((resolve) => {
    if (window.customElements.get('multiple-media-with-text')) {
      resolve();
    } else {
      const checkInterval = setInterval(() => {
        if (window.customElements.get('multiple-media-with-text')) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    }
  });
}

waitForBaseElement().then(() => {
  
class VariantResponsiveMediaWithText extends window.customElements.get('multiple-media-with-text') {
  constructor() {
    super();
    this.productId = this.getAttribute('product');
    this.sectionId = this.closest('.shopify-section')?.id || this.getAttribute('section-id');
    this.originalContent = null;
    this.isLoading = false;
  }

  connectedCallback() {
    // Call the parent's connectedCallback to preserve existing functionality
    super.connectedCallback();
    
    // Store the original content when first connected
    if (!this.originalContent) {
      this.originalContent = this.innerHTML;
    }

    // Listen for variant change events
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));
  }

  disconnectedCallback() {
    // Clean up event listener
    document.removeEventListener('variant:change', this.handleVariantChange.bind(this));
  }

  async handleVariantChange(event) {
    // Since variant:change event doesn't include productId, we'll assume it's for our product
    // if we're on a product page. For collection pages, we'd need a different approach.
    const isProductPage = window.location.pathname.includes('/products/');
    
    if (!isProductPage) {
      return;
    }

    const newVariantId = event.detail.variant?.id;
    
    if (!newVariantId) {
      // No variant selected, revert to original content
      this.innerHTML = this.originalContent;
      return;
    }

    // Prevent multiple simultaneous requests
    if (this.isLoading) {
      return;
    }

    try {
      this.isLoading = true;
      await this.updateContent(newVariantId);
    } catch (error) {
      console.error('Error updating variant-responsive content:', error);
      // Fallback to original content on error
      this.innerHTML = this.originalContent;
    } finally {
      this.isLoading = false;
    }
  }

  async updateContent(variantId) {
    // Get the current page URL and add variant parameter
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('variant', variantId);

    try {
      // Fetch the page with the new variant
      const response = await fetch(currentUrl.toString());
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the corresponding element in the fetched content by section ID
      let newElement = null;
      
      if (this.sectionId) {
        // First try to find the exact section, then the element within it
        const sectionElement = doc.querySelector(`#${this.sectionId}`);
        if (sectionElement) {
          newElement = sectionElement.querySelector(`variant-responsive-media-with-text[product="${this.productId}"]`);
        }
      }
      
      // Fallback: if no section ID or not found, try direct selector (but this might cause the duplication issue)
      if (!newElement) {
        const allElements = doc.querySelectorAll(`variant-responsive-media-with-text[product="${this.productId}"]`);
        // If there are multiple elements, we can't safely determine which one matches this instance
        if (allElements.length === 1) {
          newElement = allElements[0];
        }
      }

      if (newElement) {
        // Update with the new content
        this.innerHTML = newElement.innerHTML;
        
        // Reinitialize any scripts that might be needed for the new content
        this.reinitializeScripts();
      } else {
        // If no variant-specific content found, show original
        this.innerHTML = this.originalContent;
      }

    } catch (error) {
      console.error('Error fetching variant content:', error);
      throw error;
    }
  }

  reinitializeScripts() {
    // Reinitialize video players
    const videos = this.querySelectorAll('video');
    videos.forEach(video => {
      if (video.hasAttribute('autoplay')) {
        video.play().catch(e => {
          // Autoplay might be blocked, that's okay
        });
      }
    });

    // Reinitialize any other custom elements within this section
    const customElements = this.querySelectorAll('[is], :not(:defined)');
    customElements.forEach(element => {
      if (element.connectedCallback && typeof element.connectedCallback === 'function') {
        element.connectedCallback();
      }
    });

    // Trigger a custom event to notify other scripts that content has been updated
    this.dispatchEvent(new CustomEvent('variant-responsive-media-with-text:updated', {
      bubbles: true,
      detail: { element: this }
    }));
  }
}

// Register the custom element
if (!customElements.get('variant-responsive-media-with-text')) {
  customElements.define('variant-responsive-media-with-text', VariantResponsiveMediaWithText);
}

});


// Hide unavailable product combinations

document.addEventListener('DOMContentLoaded', function() {

  function show_hide_options(applicable_variants, options, option_num) {
    options.forEach(el => {
      const current_value = el.dataset.label_value;
      console.log('current_value', current_value)
      const find_item = applicable_variants.find(el => el[option_num] == current_value)

      if (!find_item) {
        el.setAttribute('hidden', true)
        el.nextElementSibling.setAttribute('hidden', true)
        
      } else {
        el.removeAttribute('hidden')
        el.nextElementSibling.removeAttribute('hidden')
        // check for variant group, show if it's there
        el.closest('.variant-group')?.removeAttribute('hidden')
      }
    })

    const variant_groups = document.querySelectorAll('.variant-group')
    if (!variant_groups.length > 0) return;

    variant_groups.forEach(el => {
      const group_inputs = el.querySelectorAll('input');
      const group_inputs_hidden = el.querySelectorAll('input[hidden]');
      el.removeAttribute('style')
      if (!group_inputs_hidden.length > 0) return;
      console.log('group_inputs.length', group_inputs.length)
      console.log('group_inputs_hidden.length', group_inputs_hidden.length)
      if (group_inputs.length === group_inputs_hidden.length) {
        el.setAttribute('hidden', true)
      }
    })
  }
  
  function hide_unavailable_combinations() {
    
    const product_data_el = document.querySelector('[data-product]')
    console.log('product_data_el', product_data_el)
    if (!product_data_el) return;

    // get product data
    const product_data = JSON.parse(product_data_el.textContent);
    console.log('product_data', product_data)


    // get first option value
    const current_option = document.querySelector('[data-option-position="1"]:checked')
    const current_option_label = current_option.dataset.label_value
    console.log('current_option', current_option)
    console.log('current_option_label', current_option_label)

    // get variants with current first option value
    const applicable_variants = product_data.variants.filter(item =>
      item.option1 === current_option_label
    );
    console.log('applicable_variants', applicable_variants)

    // hide 
    // maybe if more options come in the future we can be more generic
    /* const other_options = document.querySelectorAll('[data-option-position]:not([data-option-position="1"])')
    console.log('other_options: ', other_options)
    if (other_options.length > 0) {
      other_options.forEach(el => {
        const current_value = el.dataset.label_value;
        console.log('current_value', current_value)
      })
    } */
    // but for now let's be specific to matching option2 and option 3 just in case something simple like "Black" occurs in both
    const option2 = document.querySelectorAll('[data-option-position="2"]')
    const option3 = document.querySelectorAll('[data-option-position="3"]')
    console.log('option2: ', option2)

    if (option2.length > 0) show_hide_options(applicable_variants, option2, 'option2')
    if (option3.length > 0) show_hide_options(applicable_variants, option3, 'option3')
  }
  
  function showVariantGroups() {
    const variant_groups = document.querySelectorAll('.variant-group');
    if (variant_groups.length > 0) {
      variant_groups.forEach(el => {
        console.log('el:::', el)
        el.style.opacity = 1
      })
    }
  }

  function select_first_available(change_target) {
    console.log('select_first_available change_target:', change_target)

    const change_label = change_target.dataset.label_value;
    const change_options = document.querySelector(`[data-variant_group_title="${change_label}"`)
    if (!change_options) return;
    const change_options_parent = change_options.closest('.variant-group');
    if (!change_options_parent) return;
    const first_available_option = change_options_parent.querySelector('input:not([disabled])')
    first_available_option?.click();
    first_available_option.dispatchEvent(new Event("change", { bubbles: true }))
  }

  const variant_picker = document.querySelector('[data-hide_unavailable_combinations]')
  if (!variant_picker) return;
  hide_unavailable_combinations();
  setTimeout(showVariantGroups,1)
  document.addEventListener('variant:change', (e) => {
    showVariantGroups();
    hide_unavailable_combinations();
  });

  document.addEventListener('change', (e) => {
    //console.log('change event:', e.target)
    const change_target = e.target.closest('input')
    const check_hide_unavailable_combos = document.querySelector('[data-hide_unavailable_combinations]')

    if (!change_target || !change_target.dataset.optionPosition === '1' || !change_target.dataset.label_value || !check_hide_unavailable_combos) return;

    select_first_available(change_target);

  })


});
